"use strict";

/**
 * A string containing all alphanumeric characters.
 * @private
 * @constant {string}
 */
const CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

/**
 * Generates a random string of the specified length using characters from CHARACTERS.
 * @param {number} length - The length of the random string to generate.
 * @returns {string} A random string of length `length`.
 */
export function generateRandomText(length: number): string {
    let result = '';
    const charactersLength = CHARACTERS.length;
    for (let i = 0; i < length; i++) {
        result += CHARACTERS.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
}

/**
 * A GeoJSON Point or LineString coordinate.
 */
type Coordinate = [number, number];

/**
 * A simplified GeoJSON LineString Feature type used for offsetting.
 */
interface LineStringFeature {
    type: "Feature";
    geometry: {
        type: "LineString";
        coordinates: Coordinate[];
    };
    properties: Record<string, any>;
}

/**
 * Generates a smoother parallel offset version of a LineString by densifying the input line.
 * This avoids self-intersections and uneven distances on curves without external libraries.
 *
 * ⚠️ Skips any segments of zero length to avoid producing NaN or Infinite coordinates.
 *
 * @param line - A GeoJSON LineString feature in projected coordinates (e.g., EPSG:3857).
 * @param distance - The perpendicular distance to offset the line. Positive for right, negative for left.
 * @param steps - How many segments to add between original points for smoothing.
 * @returns A new LineString feature offset from the original.
 */
export function getLineOffsetMeters(line: LineStringFeature, distance: number, steps: number = 5): LineStringFeature {
    const coords = line.geometry.coordinates;
    const offsetCoords: Coordinate[] = [];
    const sign = distance < 0 ? -1 : 1;
    const offsetDistance = Math.abs(distance);
    const safeAngleThreshold = 0.5; // radians ~28.6°
    const maxSafeAngle = 2.8; // radians ~160°

    const interpolate = (a: Coordinate, b: Coordinate, t: number): Coordinate => [
        a[0] + (b[0] - a[0]) * t,
        a[1] + (b[1] - a[1]) * t
    ];

    const getNormal = (p1: Coordinate, p2: Coordinate): Coordinate => {
        const dx = p2[0] - p1[0];
        const dy = p2[1] - p1[1];
        const len = Math.sqrt(dx * dx + dy * dy);
        return len === 0 ? [0, 0] : [-(dy / len), dx / len];
    };

    // 1. Densify line (adaptive: more dense at sharp turns)
    const densified: Coordinate[] = [];
    for (let i = 0; i < coords.length - 1; i++) {
        const p1 = coords[i];
        const p2 = coords[i + 1];
        const dx = p2[0] - p1[0];
        const dy = p2[1] - p1[1];
        const len = Math.sqrt(dx * dx + dy * dy);
        const segSteps = Math.max(steps, Math.ceil(len / offsetDistance));
        for (let s = 0; s <= segSteps; s++) {
            densified.push(interpolate(p1, p2, s / segSteps));
        }
    }

    const filtered = densified.filter((p, i, arr) =>
        i === 0 || p[0] !== arr[i - 1][0] || p[1] !== arr[i - 1][1]
    );

    // 2. Offset each point with corner spike and U-turn avoidance
    for (let i = 0; i < filtered.length; i++) {
        let normal: Coordinate;

        if (i === 0) {
            normal = getNormal(filtered[i], filtered[i + 1]);
        } else if (i === filtered.length - 1) {
            normal = getNormal(filtered[i - 1], filtered[i]);
        } else {
            const n1 = getNormal(filtered[i - 1], filtered[i]);
            const n2 = getNormal(filtered[i], filtered[i + 1]);

            const dot = n1[0] * n2[0] + n1[1] * n2[1];
            const angle = Math.acos(Math.min(Math.max(dot, -1), 1));

            if (isNaN(angle) || angle < safeAngleThreshold || angle > maxSafeAngle) {
                continue; // skip sharp spike or near U-turn to prevent loop
            }

            normal = [
                (n1[0] + n2[0]) / 2,
                (n1[1] + n2[1]) / 2
            ];
            const len = Math.sqrt(normal[0] * normal[0] + normal[1] * normal[1]);
            if (len !== 0) {
                normal = [normal[0] / len, normal[1] / len];
            }
        }

        offsetCoords.push([
            filtered[i][0] + normal[0] * offsetDistance * sign,
            filtered[i][1] + normal[1] * offsetDistance * sign
        ]);
    }

    // 3. Clean geometry: remove very short segments or near-zero turns
    const cleanedCoords: Coordinate[] = [];
    cleanedCoords.push(offsetCoords[0]);

    for (let i = 1; i < offsetCoords.length - 1; i++) {
        const a = cleanedCoords[cleanedCoords.length - 1];
        const b = offsetCoords[i];
        const c = offsetCoords[i + 1];

        const ab = [b[0] - a[0], b[1] - a[1]];
        const bc = [c[0] - b[0], c[1] - b[1]];

        const lenAB = Math.hypot(ab[0], ab[1]);
        const lenBC = Math.hypot(bc[0], bc[1]);

        if (lenAB < 0.01 || lenBC < 0.01) continue;

        const dot = ab[0] * bc[0] + ab[1] * bc[1];
        const angle = Math.acos(dot / (lenAB * lenBC));

        if (!isNaN(angle) && angle > safeAngleThreshold && angle < Math.PI - safeAngleThreshold) {
            cleanedCoords.push(b);
        }
    }

    cleanedCoords.push(offsetCoords[offsetCoords.length - 1]);

    // 4. Merge start and end if very close to eliminate loops
    const start = cleanedCoords[0];
    const end = cleanedCoords[cleanedCoords.length - 1];
    const dist = Math.hypot(end[0] - start[0], end[1] - start[1]);

    if (dist < offsetDistance * 1.5) {
        const avg: Coordinate = [
            (start[0] + end[0]) / 2,
            (start[1] + end[1]) / 2
        ];
        cleanedCoords[0] = avg;
        cleanedCoords[cleanedCoords.length - 1] = avg;
    }

    return {
        type: "Feature",
        geometry: {
            type: "LineString",
            coordinates: cleanedCoords
        },
        properties: {}
    };
}